import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import {
  CheckCircle, Clock, Truck, Package, MapPin, Download,
  Star, ChevronRight, AlertCircle, Loader2,
} from "lucide-react";

const STATUT_LABELS = {
  en_attente: "En attente de livraison",
  en_cours:   "En route vers vous...",
  livree:     "Livré !",
  reportee:   "Livraison reportée",
  annulee:    "Livraison annulée",
};

const STEPS = [
  { key: "en_attente", label: "Commande confirmée",     icon: Package },
  { key: "en_cours",   label: "En cours de livraison",  icon: Truck },
  { key: "livree",     label: "Livré",                  icon: CheckCircle },
];

const stepIndex = (statut) => {
  if (statut === "livree")   return 2;
  if (statut === "en_cours") return 1;
  return 0;
};

const Suivi = () => {
  const { token } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed]   = useState(null); // "confirme" | "absent"

  useEffect(() => {
    const fetchSuivi = async () => {
      try {
        const res = await axios.get(`${API}/suivi/${token}`);
        setData(res.data);
        if (res.data.confirmation_presence && res.data.confirmation_presence !== "en_attente") {
          setConfirmed(res.data.confirmation_presence);
        }
      } catch (e) {
        setError("Livraison introuvable. Le lien est peut-être expiré ou incorrect.");
      } finally {
        setLoading(false);
      }
    };
    fetchSuivi();
  }, [token]);

  const handleConfirmation = async (choice) => {
    setConfirming(true);
    try {
      await axios.put(`${API}/suivi/${token}/confirmation`, { confirmation: choice });
      setConfirmed(choice);
    } catch {
      // silently fail — still show visual feedback
      setConfirmed(choice);
    } finally {
      setConfirming(false);
    }
  };

  const downloadReceipt = async () => {
    try {
      const res = await axios.get(`${API}/livraisons/${data.livraison_id}/recu-public/${token}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `recu_topcocoon.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        weekday: "long", day: "numeric", month: "long",
      });
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle className="w-14 h-14 text-red-400 mb-4" />
        <h2 className="text-white font-bold text-xl mb-2">Lien invalide</h2>
        <p className="text-white/50 text-sm">{error}</p>
      </div>
    );
  }

  const statut     = data.statut;
  const currentStep = stepIndex(statut);
  const isSpecial  = statut === "reportee" || statut === "annulee";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex flex-col items-center py-8 px-4">

      {/* Header */}
      <div className="w-full max-w-md mb-6 text-center">
        {data.logo_url ? (
          <img
            src={data.logo_url}
            alt={data.company_name}
            className="w-16 h-16 rounded-2xl mx-auto mb-3 object-contain bg-white/10 p-2"
          />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-3">
            <span className="text-white font-bold text-lg">TC</span>
          </div>
        )}
        <h1 className="text-white font-bold text-xl tracking-tight">{data.company_name}</h1>
        <p className="text-indigo-300 text-sm mt-0.5">Suivi de votre livraison</p>
      </div>

      <div className="w-full max-w-md space-y-4">

        {/* Status Hero */}
        <div className={`rounded-2xl p-5 border backdrop-blur-sm ${
          statut === "livree"   ? "bg-green-500/10 border-green-400/30" :
          statut === "reportee" ? "bg-orange-500/10 border-orange-400/30" :
          statut === "annulee"  ? "bg-red-500/10 border-red-400/30" :
          statut === "en_cours" ? "bg-indigo-500/10 border-indigo-400/30" :
                                  "bg-slate-500/10 border-slate-400/30"
        }`} data-testid="statut-hero">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
              statut === "livree"   ? "bg-green-500" :
              statut === "reportee" ? "bg-orange-500" :
              statut === "annulee"  ? "bg-red-500" :
              statut === "en_cours" ? "bg-indigo-500" : "bg-slate-600"
            }`}>
              {statut === "livree"   ? <CheckCircle className="w-7 h-7 text-white" /> :
               statut === "en_cours" ? <Truck className="w-7 h-7 text-white" /> :
                                       <Clock className="w-7 h-7 text-white" />}
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase tracking-widest font-medium">Statut actuel</p>
              <p className={`font-bold text-xl mt-0.5 ${
                statut === "livree"   ? "text-green-400" :
                statut === "reportee" ? "text-orange-400" :
                statut === "annulee"  ? "text-red-400" :
                statut === "en_cours" ? "text-indigo-300" : "text-slate-300"
              }`} data-testid="statut-label">
                {STATUT_LABELS[statut] || statut}
              </p>
              {data.date_livraison_prevue && statut !== "livree" && (
                <p className="text-white/50 text-sm mt-0.5">
                  Prévu le {formatDate(data.date_livraison_prevue)}
                  {data.heure_estimee && <span className="text-indigo-300 font-semibold"> vers {data.heure_estimee}</span>}
                </p>
              )}
              {data.date_livraison_effective && statut === "livree" && (
                <p className="text-green-400/80 text-sm mt-0.5">
                  Livré le {formatDate(data.date_livraison_effective)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Progress Timeline (only for normal statuts) */}
        {!isSpecial && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
            <div className="space-y-0">
              {STEPS.map((step, idx) => {
                const Icon  = step.icon;
                const isDone   = idx <= currentStep;
                const isActive = idx === currentStep;
                return (
                  <div key={step.key} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                        isDone ? "bg-indigo-500 border-indigo-400" : "bg-white/5 border-white/20"
                      } ${isActive && statut !== "livree" ? "animate-pulse" : ""}`}>
                        <Icon className={`w-4 h-4 ${isDone ? "text-white" : "text-white/30"}`} />
                      </div>
                      {idx < STEPS.length - 1 && (
                        <div className={`w-0.5 h-8 mt-1 ${isDone ? "bg-indigo-400/60" : "bg-white/10"}`} />
                      )}
                    </div>
                    <div className="pt-1.5 pb-6">
                      <p className={`font-medium text-sm ${isDone ? "text-white" : "text-white/30"}`}>
                        {step.label}
                      </p>
                      {isActive && statut === "en_cours" && (
                        <p className="text-indigo-300 text-xs mt-0.5">Le livreur est en chemin</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 space-y-4">
          <h3 className="text-white/70 text-xs uppercase tracking-widest font-medium">Votre commande</h3>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <Package className="w-5 h-5 text-indigo-300" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold">
                {data.details_commande || `${data.quantite}x article(s)`}
              </p>
              <p className="text-white/50 text-sm">{data.quantite} unité{data.quantite > 1 ? "s" : ""}</p>
            </div>
            {data.prix != null && (
              <span className="text-white font-bold">{data.montant_encaisse ?? data.prix} €</span>
            )}
          </div>

          <div className="h-px bg-white/10" />

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-white/50 text-xs uppercase tracking-wide mb-0.5">Adresse de livraison</p>
              <p className="text-white text-sm font-medium">{data.adresse}</p>
              <p className="text-white/60 text-sm">{data.code_postal} {data.ville}</p>
            </div>
          </div>
        </div>

        {/* Download Receipt (only when livree) */}
        {statut === "livree" && (
          <button
            onClick={downloadReceipt}
            className="w-full flex items-center justify-between p-4 bg-green-500/10 border border-green-400/30 rounded-2xl hover:bg-green-500/20 transition-all group"
            data-testid="download-receipt-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                <Download className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold text-sm">Télécharger mon reçu</p>
                <p className="text-green-400 text-xs">PDF • Reçu de livraison</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-green-400 group-hover:translate-x-1 transition-transform" />
          </button>
        )}

        {/* Confirmation de présence — uniquement si en_attente (pas en_cours ni livree) */}
        {statut === "en_attente" && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5" data-testid="confirmation-block">
            {!confirmed ? (
              <>
                <p className="text-white font-semibold mb-1">Confirmez-vous votre présence ?</p>
                <p className="text-white/50 text-sm mb-4">Cela aide le livreur à organiser sa tournée.</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleConfirmation("confirme")}
                    disabled={confirming}
                    className="flex flex-col items-center gap-2 p-4 bg-green-500/20 border border-green-400/30 rounded-xl hover:bg-green-500/30 transition-all active:scale-95"
                    data-testid="confirm-present-btn"
                  >
                    <CheckCircle className="w-8 h-8 text-green-400" />
                    <span className="text-green-300 font-semibold text-sm">Je serai là</span>
                  </button>
                  <button
                    onClick={() => handleConfirmation("absent")}
                    disabled={confirming}
                    className="flex flex-col items-center gap-2 p-4 bg-red-500/20 border border-red-400/30 rounded-xl hover:bg-red-500/30 transition-all active:scale-95"
                    data-testid="confirm-absent-btn"
                  >
                    <AlertCircle className="w-8 h-8 text-red-400" />
                    <span className="text-red-300 font-semibold text-sm">Je serai absent</span>
                  </button>
                </div>
              </>
            ) : confirmed === "confirme" ? (
              <div className="text-center">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                <p className="text-green-300 font-bold">Présence confirmée !</p>
                <p className="text-white/50 text-sm mt-1">Le livreur vous contactera 30 min avant son passage.</p>
              </div>
            ) : (
              <div className="text-center">
                <AlertCircle className="w-10 h-10 text-orange-400 mx-auto mb-2" />
                <p className="text-orange-300 font-bold">Absence signalée</p>
                <p className="text-white/50 text-sm mt-1">TopCocoon vous recontactera pour reprogrammer.</p>
              </div>
            )}
          </div>
        )}

        {/* Facebook CTA */}
        <a
          href="https://www.facebook.com/TopCocoon/reviews?locale=fr_FR"
          target="_blank"
          rel="noreferrer"
          className="w-full flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all group"
          data-testid="facebook-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">Laisser un avis</p>
              <p className="text-white/50 text-xs">Votre avis compte beaucoup pour nous</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-white/30 group-hover:translate-x-1 transition-transform" />
        </a>

        <p className="text-center text-white/25 text-xs pb-4">
          {data.company_name} • Livraison de mobilier en Belgique
        </p>
      </div>
    </div>
  );
};

export default Suivi;
